// Helper to robustly extract year from a date string (avoids timezone issues)
function extractYear(dateStr: string): string | null {
  // Try to match YYYY or YYYY-MM-DD etc.
  const m = dateStr.match(/(\d{4})/);
  if (m) return m[1];
  return null;
}

import { useEffect, useState } from "react";
import "./PlotFlyout.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useParameterContext } from "../types/ParameterContext";
import { useClickContext } from "../types/ClickContext";
import Plot from "react-plotly.js";
import { getFrequencyCurve, getTimeSeriesDownsampled, getTimeSeries,getGeoJsonTimeSeriesDownsampled,getGeoJsonTimeSeries, getGeoJsonFrequency } from "../utils/restapi";
import type { Data, PlotMouseEvent, ScatterData } from "plotly.js";
import type { PlotType } from "plotly.js";

type RawSeries = Partial<ScatterData>;

type FreqData = {
  units: string;
  returnPeriod: number[];
  exceedanceProbability: number[];
  rank: number[];
  max: number[];
  name: string;
  year: number[];
  year_t: number[];
  max_t: number[];
};

type SeriesItem = {
  scenario: string;
  rawSeries: RawSeries;
  monthTimeSeries: RawSeries;
  freqData: FreqData;
};

type ParamSet = {
  parameter: string;
  series: SeriesItem[];
};




const PlotFlyout: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [plotData, setPlotData] = useState<ParamSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [plotMode, setPlotMode] = useState<"raw" | "monthavg" | "frequency" | "exceedance">(
    "exceedance"
  );
  // Store x axis ranges by plot mode
  const [xAxisRanges, setXAxisRanges] = useState<{
    [mode in typeof plotMode]?: [number, number];
  }>({});
  // Keep current time visible checkbox state
  const [keepCurrentTimeVisible, setKeepCurrentTimeVisible] = useState(false);
  // Store y axis ranges by parameter and mode
  const [yAxisRanges, setYAxisRanges] = useState<{
    [key: string]: [number, number]; // key = `${parameter}__${mode}`
  }>({});

  const [prevTime, setPrevTime] = useState<number>(0);
  const handlePlotClick = (event: Readonly<PlotMouseEvent>) => {
    const time = event.points?.[0]?.x;
    if (time && plotMode === "raw"  && typeof setPlotSelectedTime === "function") {
       setPlotSelectedTime(String(time));
    }
  };


  const { scenario, parameter, time, plotSelectedTime, setPlotSelectedTime } = useParameterContext();
  const { clickData } = useClickContext();

  useEffect(() => {
    if (
      scenario.length === 0 ||
      parameter.length === 0 ||
      !clickData ||
      !clickData.lat ||
      !clickData.lon
    )
      return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const allParamData = await Promise.all(
          parameter.map(async (param) => {
            const series = await Promise.all(
              scenario.map(async (s) => {
                

                const monthRes =  clickData.featureTypeName === undefined ? await getTimeSeriesDownsampled(
                      s,
                      param,
                      clickData.lat,
                      clickData.lon,
                      1266 // 1266 is the max points for downsampling
                    )
                    : await getGeoJsonTimeSeriesDownsampled(
                      s,
                      param,
                      clickData.featureTypeName? clickData.featureTypeName : "",
                      clickData.featureProperties?.OBJECTID,
                      1266 // 1266 is the max points for downsampling
                    );
                
                const ts = clickData.featureTypeName === undefined ? await getTimeSeries(
                      s,
                      param,
                      clickData.lat,
                      clickData.lon
                    ) : await getGeoJsonTimeSeries(
                      s,
                      param,
                      clickData.featureTypeName? clickData.featureTypeName : "",
                      clickData.featureProperties?.OBJECTID
                    );

                const freq = clickData.featureTypeName === undefined ?await getFrequencyCurve(
                  s,
                  param,
                  clickData.lat,
                  clickData.lon
                ) : await getGeoJsonFrequency(
                      s,
                      param,
                      clickData.featureTypeName? clickData.featureTypeName : "",
                      clickData.featureProperties?.OBJECTID
                    );
                const monthlyTimeSeriesData = await monthRes.json();
                const freqDataJson = await freq.json();
                const timeSeriesData = await ts.json();
                
                const monthtimestampes = monthlyTimeSeriesData.time.map((ms: number) =>
                  new Date(ms).toISOString()
                );
                const timestamps = timeSeriesData.time.map((v: number) =>new Date(v).toISOString());
                return {
                  scenario: s,
                  rawSeries: {
                    x: timestamps,
                    y: timeSeriesData.value,
                    type: "scattergl" as PlotType,
                    mode: "lines+markers" as const,
                    name: `${param} (${s})`,
                  },
                  monthTimeSeries: {
                    x: monthtimestampes,
                    y: monthlyTimeSeriesData.value,
                    type: "scattergl" as PlotType,
                    mode: "lines+markers" as const,
                    name: `${param} (${s})`,
                  },
                  freqData: {
                    returnPeriod: freqDataJson.returnPeriod,
                    exceedanceProbability: freqDataJson?.exceedanceProbability??[],
                    rank: freqDataJson.rank,
                    max: freqDataJson.max,
                    name: `${param} (${s})`,
                    year: freqDataJson.year,
                    year_t: freqDataJson.year_t,
                    max_t: freqDataJson.max_t,
                    units: freqDataJson.unit || "unknown",
                  },
                  
                };
               
              })
            );
           
            return { parameter: param, series };
          })
        );
        setPlotData(allParamData);
      } catch (error) {
        console.error("Fetch failed:", error);
        setPlotData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [scenario, parameter, clickData]);

   
  const n = plotData.length;
  const subplotHeight = 1 / n;
  const combinedTraces: Partial<Data>[] = [];
  const yAxesLayout: { [key: string]: any } = {};

  const defaultColors = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
  "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
  ];
  plotData.forEach((paramSet, i) => {
    const domainStart = 1 - (i + 1) * subplotHeight;
    const domainEnd = 1 - i * subplotHeight;
    const yaxisName = i === 0 ? "yaxis" : `yaxis${i + 1}`;

    yAxesLayout[yaxisName] = {
      domain: [domainStart, domainEnd],
      title:  { text: `${paramSet.parameter} (${paramSet.series[0].freqData.units})` },
      anchor: "x",
      zeroline: false,
      showline: true,
      showticklabels: true,
    };
    paramSet.series.forEach((seriesItem,y) => {
    let trace: Partial<Data>;

    const color = defaultColors[y%defaultColors.length]
    if (plotMode === "raw") {
      trace = {
        ...seriesItem.rawSeries,
        marker: { color },
        line: { color },
        yaxis: i === 0 ? "y1" : `y${i + 1}`,
      };
    } else if (plotMode ==="monthavg") {
      trace = {
        ...seriesItem.monthTimeSeries,
        marker: { color },
        line: { color },
        yaxis: i === 0 ? "y1" : `y${i + 1}`,
      };
    }else if (plotMode === "frequency") {
      trace = {
        x: seriesItem.freqData.year_t,
        y: seriesItem.freqData.max_t,
        type: "bar",
        marker: { color },
        line: { color },

        name: seriesItem.freqData.name,
        yaxis: i === 0 ? "y1" : `y${i + 1}`,
      };
    } else {
      trace = {
        x: seriesItem.freqData.exceedanceProbability,
        y: seriesItem.freqData.max,
        marker: { color },
        line: { color },
        type: "scatter",
        mode: "lines+markers",
        name: seriesItem.freqData.name,
        yaxis: i === 0 ? "y1" : `y${i + 1}`,
      };
    }

    combinedTraces.push(trace);
  });


  });

  // Add current time line if applicable
  if ((plotMode === "raw" || plotMode === "monthavg") && time) {
    plotData.forEach((paramSet, i) => {
      const yaxis = i === 0 ? "y1" : `y${i + 1}`;
      let minY = Infinity;
      let maxY = -Infinity;
      paramSet.series.forEach((s) => {
        const ys =
          (plotMode === "raw" ? s.rawSeries.y : s.monthTimeSeries.y) ?? [];
        for (const y of ys) {
          if (typeof y === "number" && !isNaN(y)) {
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      });
      if (!isFinite(minY)) minY = 0;
      if (!isFinite(maxY)) maxY = 1;
      combinedTraces.unshift({
        x: [time, time],
        y: [minY, maxY],
        type: "scatter",
        mode: "lines",
        name: "Current Time",
        line: { color: "red", dash: "dash" },
        hoverinfo: "none",
        yaxis: yaxis,
        showlegend: i === 0, // only first one gets legend
      });
    });
  }

  // Add vertical red line for the year of the current date in frequency mode
  if (plotMode === "frequency" && time) {
    // Extract year robustly from time string
    const yearStr = extractYear(time);
    if (yearStr) {
      plotData.forEach((paramSet, i) => {
        const yaxis = i === 0 ? "y1" : `y${i + 1}`;
        // Find min/max y for this subplot
        let minY = Infinity;
        let maxY = -Infinity;
        paramSet.series.forEach((s) => {
          const ys = s.freqData.max_t ?? [];
          for (const y of ys) {
            if (typeof y === "number" && !isNaN(y)) {
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        });
        if (!isFinite(minY)) minY = 0;
        if (!isFinite(maxY)) maxY = 1;
        // Add a vertical line at the year (as string, for category axis)
        combinedTraces.unshift({
          x: [yearStr, yearStr],
          y: [0, maxY],
          type: "scatter",
          mode: "lines",
          name: "Current Year",
          line: { color: "red", dash: "dash" },
          hoverinfo: "none",
          yaxis: yaxis,
          showlegend: false,
        });
      });
    }
  }

  const asYAxisName = (axis: string): "y" | "y2" | "y3" | "y4" | "y5" | "y6"=> axis as any;
  // If keepCurrentTimeVisible is checked, adjust x axis range to keep current time centered
  let adjustedXAxisRange = xAxisRanges[plotMode];

  // Move axis adjustment logic to useEffect to avoid infinite re-renders
  useEffect(() => {
    if (
      keepCurrentTimeVisible &&
      typeof time === "string" &&
      xAxisRanges[plotMode] &&
      !isNaN(Date.parse(time)) &&
      Date.parse(time) !== prevTime
    ) {

      const yearStr = extractYear(time);
      const t = plotMode === "frequency"
        ? (yearStr !== null ? parseInt(yearStr) : 0)
        : new Date(time).getTime();
      let [x0, x1] = xAxisRanges[plotMode]!;
      if (typeof x0 === "string" && !isNaN(Date.parse(x0))) x0 = new Date(x0).getTime();
      if (typeof x1 === "string" && !isNaN(Date.parse(x1))) x1 = new Date(x1).getTime();
      let newX0 = x0, newX1 = x1;
      //see if time moved outside of range and recompute new range
      if (t < x0) {
        //t is less that x0 so time or range needs to be adjusted to keep current time visible
        const width = x1 - x0;
        newX0 = x0 - width;
        newX1 = x0;
        if (t < newX0) {
          if(prevTime < x0 || prevTime > x1) {
            //prevTime is so the "offset" calculation will be off. This is supposed to be a check to see if time changes 
            //is outside of the current range then we try slide the range to keep current time visible is the same relative position
            newX1 = t + (width/2);
            newX0 = t - (width/2);

          } else {
            const offset = x1 - prevTime;
            newX1 = t + offset;
            newX0 = newX1 - width;
          }
            
          
        }
      } else if (t > x1) {
        const width = x1 - x0;
        newX0 = x1;
        newX1 = x1 + width;
        if (t > newX1) {
           if(prevTime < x0 || prevTime > x1) {
            //prevTime is so the "offset" calculation will be off. This is supposed to be a check to see if time changes 
            //is outside of the current range then we try slide the range to keep current time visible is the same relative position
            newX1 = t + (width/2);
            newX0 = t - (width/2);

          } else {
            const offset = prevTime - x0;
            newX0 = t - offset;
            newX1 = newX0 + width;
          }
        }
        
      }
      if (newX0 !== x0 || newX1 !== x1) {
        setXAxisRanges(prev => ({
          ...prev,
          [plotMode]: [newX0, newX1],
        }));
      }
      setPrevTime(t);
    }
  }, [keepCurrentTimeVisible, time, xAxisRanges, plotMode, prevTime]);

  // Build layout and apply axis ranges if they exist
  const layout: any = {
    height: 400 * n,
    margin: { t: 30, l: 50, r: 10, b: 50 },
    xaxis: {
      title: {  text:
          plotMode === "raw"
            ? "Time"
            : plotMode === "monthavg"
            ? "Time"
            : plotMode === "frequency"
            ? "Annual Maximum"
            : "Annual Exceedance (%)" },
      domain: [0, 1],
      zeroline: false,
      anchor: asYAxisName(`y${n}`),
      showticklabels: true,
      // Set xaxis type to category for frequency mode (years)
      //  ...(plotMode === "frequency" ? { type: "category" } : {}),
      // Apply x axis range if exists
      ...(adjustedXAxisRange
        ? { range: adjustedXAxisRange }
        : {}),
    },
    ...yAxesLayout,
    legend: { orientation: "h", x: 0, y: -0.3 } as Partial<Plotly.Legend>,
    hovermode: "x unified" as const,
    showlegend: true,
  };
  // Apply y axis ranges if they exist (by parameter and mode)
  plotData.forEach((paramSet, i) => {
    const yaxisName = i === 0 ? "yaxis" : `yaxis${i + 1}`;
    const yKey = `${paramSet.parameter}__${plotMode}`;
    if (yAxisRanges[yKey]) {
      layout[yaxisName] = {
        ...layout[yaxisName],
        range: yAxisRanges[yKey],
      };
    }
  });
  
  const handleRelayout = (event: any) => {
    // event contains new axis ranges, e.g. event['xaxis.range[0]'], event['xaxis.range[1]']
    // Also handles autorange events
    console.log("Plotly relayout event:", event);
    // Handle x axis range by mode
    let x0 = event["xaxis.range[0]"];
    let x1 = event["xaxis.range[1]"];
    if (event["xaxis.autorange"] === true) {
      // If autorange, try to get new extents if provided
      x0 = event["xaxis.min"] ?? x0;
      x1 = event["xaxis.max"] ?? x1;
    }
    else if (Array.isArray(event["xaxis.range"]) && event["xaxis.range"].length > 0) {
      // If range is an array, use the first two elements
      x0 = event["xaxis.range"][0];
      x1 = event["xaxis.range"][1];
    }
    setXAxisRanges((prev) => ({
      ...prev,
      [plotMode]: [x0, x1],
    }));
    // Handle y axis ranges by parameter and mode
    plotData.forEach((paramSet, i) => {
      const yaxisKey = i === 0 ? "yaxis" : `yaxis${i + 1}`;
      let y0 = event[`${yaxisKey}.range[0]`];
      let y1 = event[`${yaxisKey}.range[1]`];
      if (event["yaxis.autorange"] === true) {
        // If autorange, try to get new extents if provided
        y0 = event[`${yaxisKey}.min`] ?? y0;
        y1 = event[`${yaxisKey}.max`] ?? y1;
      } else if (Array.isArray(event[`${yaxisKey}.range`]) && event[`${yaxisKey}.range`].length > 0) {
        // If range is an array, use the first two elements
        y0 = event[`${yaxisKey}.range`][0];
        y1 = event[`${yaxisKey}.range`][1];
      }
      // In autorange event, Plotly does not provide min/max, so just use range[0]/range[1] if present
      const yKey = `${paramSet.parameter}__${plotMode}`;
      setYAxisRanges((prev) => ({
        ...prev,
        [yKey]: [y0, y1],
      }));
    });
  };


  return (
    <div
      className={`fixed top-0 right-0 h-full bg-white shadow-lg z-[1001] transition-all duration-300 ease-in-out ${
        open ? "w-[700px]" : "w-1"
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="absolute top-1/2 left-[-20px] transform -translate-y-1/2 bg-blue-600 text-white w-8 h-8 flex items-center justify-center rounded-l-md shadow-md z-50"
      >
        {open ? <ChevronRight size={25} /> : <ChevronLeft size={25} />}
      </button>

      {open && (
        <div className="p-4 space-y-4 flex flex-col h-full overflow-y-auto">
          <h2 className="text-xl font-bold">Time Series Info</h2>
          <div className="text-sm text-gray-700">
            <p>
              <strong>Latitude:</strong> {clickData?.lat.toFixed(4)}
            </p>
            <p>
              <strong>Longitude:</strong> {clickData?.lon.toFixed(4)}
            </p>
            {(() => {
              const name =
                clickData?.featureProperties?.NAME ||
                clickData?.featureProperties?.Name ||
                null;
              return name ? (
                <p>
                  <strong>Feature Name:</strong> {name}
                </p>
              ) : null;
            })()}
          </div>

          <div className="flex space-x-4 items-center">
            {["raw", "monthavg", "frequency", "exceedance"].map((mode) => (
              <label key={mode} className="flex items-center space-x-2">
                <input
                  type="radio"
                  value={mode}
                  checked={plotMode === mode}
                  onChange={() => setPlotMode(mode as any)}
                />
                <span>
                  {mode === "raw"
                    ? "Time Series"
                    : mode === "monthavg"
                    ? "Monthly Average"
                    : mode === "frequency"
                    ? "Annual Maximum"
                    : "Annual Exceedance"}
                </span>
              </label>
            ))}
          </div>
          <div className="flex-grow space-y-8">
            <div style={{ flex: 1 }}>
              {loading ? (
                <p>Loading time series...</p>
              ) : !plotData.length ? (
                <p>No data to plot.</p>
              ) : (
                <div>
                  <Plot
                    onClick={handlePlotClick}
                    data={combinedTraces}
                    layout={layout}
                    style={{ width: "100%", height: layout.height }}
                    config={{'scrollZoom': true}}
                    onRelayout={handleRelayout}
                  />
                  <div style={{ textAlign: "left", marginTop: "8px" }}>
                    <label className="flex items-center space-x-2 ml-4">
                      <input
                        type="checkbox"
                        checked={keepCurrentTimeVisible}
                        onChange={(e) =>
                          setKeepCurrentTimeVisible(e.target.checked)
                        }
                      />
                      <span>Keep current time visible on plot</span>
                    </label>
                    {(plotMode === "raw" || plotMode === "monthavg") && (
                      <button
                        className="snap-water-year-btn mt-2 ml-0 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded shadow disabled:bg-gray-300 disabled:text-gray-500"
                        disabled={!time}
                        onClick={() => {
                          if (!time) return;
                          // Extract year from current time
                          const yearStr = extractYear(time);
                          if (!yearStr) return;
                          let year = parseInt(yearStr, 10);
                          // Water year: 01OCT of previous year to 01OCT of current year
                          let start = new Date(`${year - 1}-10-01T00:00:00Z`).getTime();
                          let end = new Date(`${year}-10-01T00:00:00Z`).getTime();
                          // Current time as ms
                          let t = new Date(time).getTime();
                          // If current time is before start, shift window backward
                          while (t < start) {
                            year--;
                            start = new Date(`${year - 1}-10-01T00:00:00Z`).getTime();
                            end = new Date(`${year}-10-01T00:00:00Z`).getTime();
                          }
                          // If current time is after end, shift window forward
                          while (t >= end) {
                            year++;
                            start = new Date(`${year - 1}-10-01T00:00:00Z`).getTime();
                            end = new Date(`${year}-10-01T00:00:00Z`).getTime();
                          }
                          setXAxisRanges(prev => ({
                            ...prev,
                            raw: [start, end],
                            monthavg: [start, end],
                          }));
                        }}
                      >Snap To Water Year</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotFlyout;
